/**
 * Resolving a trainee's DAY: a frozen menu plus the diff they wrote against it.
 *
 * ── what this is ──
 *
 * `nutritionV2Model.ts` beside this file is the BUILDER's half — a menu as the trainer authors it.
 * This is the other half: what the trainee did to it. The two documents on `nutrition_v2_day_log`
 * are the plan (a whole `MenuV2`) and the log (a diff, keyed by the plan's uids), and neither is
 * legible alone. A tick is a uid; a swap is a uid pointing at another uid; an added food is filed
 * under a MEAL's uid. Turning that pair into rows a person can read is all this file does.
 *
 * ── it is a PORT, and the original is the spec ──
 *
 * Every function here has a twin in `progress-coach/src/helpers/nutritionV2Model.ts`, and they must
 * agree: the trainee and their coach looking at the same Tuesday and seeing different food is the
 * one failure this feature cannot survive. Kept in its own file precisely so it can be diffed
 * against that one in a single pass — do not fold it into the builder model, which has no use for
 * any of it.
 *
 * The one deliberate divergence is arithmetic, and it is documented on {@link dayActualTotals}.
 *
 * ── the counting rule, which everything below rests on ──
 *
 * A substituted row is counted IN PLACE OF the food it replaced; an added row is counted ON TOP.
 * Neither is ever counted twice, and an alternative meal's items replace their parent's rather than
 * joining them. Get that wrong and the day's totals quietly stop matching the trainee's app.
 */

import {
  dominantMacroByGrams,
  emptyMacros,
  isMealBuilderType,
  type FoodItemV2,
  type MacroSet,
  type MacroTotals,
  type MealV2,
  type MenuV2,
  type SubGroupV2,
} from "./nutritionV2Model";

// ── the log document ────────────────────────────────────────────────────────

/**
 * The trainee's diff against the frozen plan.
 *
 * Declared by hand rather than aliased to the generated `DayLogV2Dto`, whose every field and every
 * map is optional: the resolver indexes these maps directly, and threading `?? {}` through forty
 * call sites to satisfy a type that is never actually partial on the wire is worse than flattening
 * the optionality once, at the service boundary (see `FoodLogService.getDayLog`).
 *
 * An empty log is a legitimate, fully-unreported day. **Absence of an entry means "as planned",
 * never "unknown"** — which is why nothing here treats a missing key as missing information.
 */
export type DayLogV2 = {
  dateKey: string;
  chosenMenuId: string | null;
  /** meal uid → the alternative meal taken instead of it */
  chosenAlternativeByMeal: Record<string, string>;
  /** item uid → the substitution group taken instead of it */
  chosenSubstitutionByFood: Record<string, string>;
  /** item uid → the serving it was measured in */
  chosenServingByFood: Record<string, number>;
  /** item uid → a food the trainee put in its place that the trainer never authored */
  replacedFoodByUid: Record<string, FoodItemV2>;
  /** item uid → ticked */
  eaten: Record<string, boolean>;
  /** item uid → the amount really eaten, when it differs from the plan */
  actualQuantity: Record<string, number>;
  /** MEAL uid → foods the trainee added themselves */
  extraFoods: Record<string, FoodItemV2[]>;
  waterMl: number;
  /** supplement uid → taken */
  supplementsTaken: Record<string, boolean>;
};

export const createDayLog = (dateKey: string): DayLogV2 => ({
  dateKey,
  chosenMenuId: null,
  chosenAlternativeByMeal: {},
  chosenSubstitutionByFood: {},
  chosenServingByFood: {},
  replacedFoodByUid: {},
  eaten: {},
  actualQuantity: {},
  extraFoods: {},
  waterMl: 0,
  supplementsTaken: {},
});

// ── which meals a menu has at all ───────────────────────────────────────────

const slot = (uid: string, name: string, order: number): MealV2 => ({
  uid,
  name,
  timeFrom: "",
  timeTo: "",
  order,
  items: [],
  alternatives: [],
});

/**
 * The frame a menu with no meals of its own is read through. The uids matter: they are the ones
 * the trainee's app files added food under, so they have to be these exact strings.
 */
export const FALLBACK_MEAL_SLOTS: MealV2[] = [
  slot("nv2_slot_breakfast", "ארוחת בוקר", 0),
  slot("nv2_slot_lunch", "ארוחת צהריים", 1),
  slot("nv2_slot_dinner", "ארוחת ערב", 2),
  slot("nv2_slot_snacks", "ביניים ונשנושים", 3),
];

/**
 * THE resolver for "which meals does this day have" — nothing should reach for `menu.meals`.
 *
 * A macro-goals menu keeps the trainer's meals as a FRAME and drops what is inside them: the
 * trainer said when the trainee eats and said nothing about what. Those `items` may also be a stale
 * plan left behind by switching the menu's type, and showing them would hand back a day the trainer
 * switched off.
 */
export const menuMeals = (menu: Pick<MenuV2, "menuType" | "meals">): MealV2[] => {
  if (isMealBuilderType(menu.menuType)) return menu.meals || [];
  const frame = (menu.meals || []).map((meal) => ({ ...meal, items: [], alternatives: [] }));
  return frame.length ? frame : FALLBACK_MEAL_SLOTS;
};

// ── per-row state ───────────────────────────────────────────────────────────

export type EatenState = "none" | "part" | "all";
export type DeviationDirection = "over" | "under" | "on";

/** Below this, an amount counts as "as planned" rather than a deviation worth flagging. */
export const DEVIATION_TOLERANCE = 0.02;

export const isEaten = (item: FoodItemV2, log: DayLogV2): boolean => log.eaten[item.uid] === true;

/** What the trainee actually ate — their reported amount, or the plan if they left it alone. */
export const actualQuantity = (item: FoodItemV2, log: DayLogV2): number => {
  const reported = log.actualQuantity[item.uid];
  return typeof reported === "number" ? reported : item.quantity || 0;
};

const round1 = (n: number) => Math.round(n * 10) / 10;

/** Nutrition of one serving scaled to an arbitrary quantity. */
export const nutritionAtQuantity = (item: FoodItemV2, quantity: number): MacroSet => {
  const q = Number(quantity) || 0;
  return {
    calories: Math.round((item.nutrition.calories || 0) * q),
    protein: round1((item.nutrition.protein || 0) * q),
    carbs: round1((item.nutrition.carbs || 0) * q),
    fats: round1((item.nutrition.fats || 0) * q),
    fiber: round1((item.nutrition.fiber || 0) * q),
  };
};

/**
 * How far the reported amount is from the planned one. `kcal` is signed and is the calorie cost of
 * the difference, so a row can print "+64" rather than making the reader do the subtraction.
 */
export const foodDeviation = (
  item: FoodItemV2,
  actual: number,
): { pct: number; kcal: number; direction: DeviationDirection } => {
  const planned = item.quantity || 0;
  if (!planned) return { pct: 0, kcal: 0, direction: "on" };
  const pct = (actual - planned) / planned;
  return {
    pct,
    kcal: Math.round((item.nutrition.calories || 0) * (actual - planned)),
    direction: Math.abs(pct) <= DEVIATION_TOLERANCE ? "on" : pct > 0 ? "over" : "under",
  };
};

// ── resolving a meal into rows ──────────────────────────────────────────────

/**
 * Where a row came from. Every food is in exactly one of three states, and the card is legible only
 * while they stay distinguishable:
 *
 *   'planned'     the trainer's food, as written
 *   'substituted' the trainee took one of the trainer's approved swaps for it
 *   'added'       the trainee ate something the menu never mentioned
 */
export type RowSource = "planned" | "substituted" | "added";

export type MealRow = {
  item: FoodItemV2;
  source: RowSource;
  /**
   * The planned food this row stands in for: the same object for a plain planned row, the food that
   * was replaced for a substituted one, and itself for an addition, which stands in for nothing.
   */
  origin: FoodItemV2;
  /** the substitution group taken, when `source` is 'substituted' */
  groupUid: string | null;
};

/**
 * The food as the trainee measured it.
 *
 * Named apart from the builder's `withServing(item, servingSizeId)`, which answers a different
 * question — that one changes a serving, this one reads the one already chosen. Applied at row
 * level so the row, the meal total and the day total are all computed from the same serving.
 */
export const withLoggedServing = (item: FoodItemV2, log: DayLogV2): FoodItemV2 => {
  const id = (log.chosenServingByFood || {})[item.uid];
  if (id == null || id === item.servingSizeId) return item;
  const serving = (item.servings || []).find((sv) => sv.id === id);
  if (!serving) return item;
  return { ...item, servingSizeId: serving.id, servingName: serving.servingName, nutrition: serving.nutrition };
};

/** The chosen substitution for one planned food, if any. */
export const chosenSubGroup = (item: FoodItemV2, log: DayLogV2): SubGroupV2 | null => {
  const uid = (log.chosenSubstitutionByFood || {})[item.uid];
  if (!uid) return null;
  return (item.substitutions || []).find((g) => g.uid === uid) || null;
};

/**
 * The meal the trainee actually worked from: the alternative they picked, or the main one. An
 * alternative REPLACES the meal — it is never added to it.
 */
const baseItems = (meal: MealV2, log: DayLogV2): FoodItemV2[] => {
  const altUid = log.chosenAlternativeByMeal[meal.uid];
  if (!altUid) return meal.items || [];
  const alt = (meal.alternatives || []).find((a) => a.uid === altUid);
  return alt ? alt.items : meal.items || [];
};

/**
 * The planned side of the meal, with every chosen substitution applied.
 *
 * One planned food can expand into several rows — a group may hold more than one food ("bread AND
 * banana instead of oats"). The food it replaced does not appear.
 */
export const plannedRows = (meal: MealV2, log: DayLogV2): MealRow[] =>
  baseItems(meal, log).flatMap<MealRow>((base) => {
    const item = withLoggedServing(base, log);
    const group = chosenSubGroup(item, log);
    if (group) {
      return group.items.map((sub) => ({
        item: withLoggedServing(sub, log),
        source: "substituted" as const,
        origin: item,
        groupUid: group.uid,
      }));
    }
    // a food the trainee put in its place that the trainer never authored
    const replacement = (log.replacedFoodByUid || {})[item.uid];
    if (replacement) {
      return [
        {
          item: withLoggedServing(replacement, log),
          source: "substituted" as const,
          origin: item,
          groupUid: null,
        },
      ];
    }
    return [{ item, source: "planned" as const, origin: item, groupUid: null }];
  });

export const plannedItems = (meal: MealV2, log: DayLogV2): FoodItemV2[] =>
  plannedRows(meal, log).map((r) => r.item);

export const extraItems = (meal: MealV2, log: DayLogV2): FoodItemV2[] => log.extraFoods[meal.uid] || [];

/** Every row the card renders, each knowing which of the three states it is in. */
export const mealRowDetails = (meal: MealV2, log: DayLogV2): MealRow[] => [
  ...plannedRows(meal, log),
  ...extraItems(meal, log).map<MealRow>((base) => {
    const item = withLoggedServing(base, log);
    return { item, source: "added", origin: item, groupUid: null };
  }),
];

export const mealRows = (meal: MealV2, log: DayLogV2): FoodItemV2[] =>
  mealRowDetails(meal, log).map((r) => r.item);

// ── totals ──────────────────────────────────────────────────────────────────

const emptyTotals = (): MacroTotals => ({ ...emptyMacros(), qualityProtein: 0 });

/** Grams, not calories — the rule the food's own tag uses, kept in step with `listTotals`. */
const qualityProteinOf = (item: FoodItemV2, at: MacroSet): number =>
  dominantMacroByGrams(item.nutrition) === "protein" ? at.protein : 0;

/** Accumulate at FULL precision; the caller rounds once. See {@link dayActualTotals}. */
const addRaw = (acc: MacroTotals, item: FoodItemV2, quantity: number): MacroTotals => {
  const q = Number(quantity) || 0;
  acc.calories += (item.nutrition.calories || 0) * q;
  acc.protein += (item.nutrition.protein || 0) * q;
  acc.carbs += (item.nutrition.carbs || 0) * q;
  acc.fats += (item.nutrition.fats || 0) * q;
  acc.fiber += (item.nutrition.fiber || 0) * q;
  if (dominantMacroByGrams(item.nutrition) === "protein") {
    acc.qualityProtein += (item.nutrition.protein || 0) * q;
  }
  return acc;
};

const rounded = (t: MacroTotals): MacroTotals => ({
  calories: Math.round(t.calories),
  protein: round1(t.protein),
  carbs: round1(t.carbs),
  fats: round1(t.fats),
  fiber: round1(t.fiber),
  qualityProtein: round1(t.qualityProtein),
});

/** What this meal asks for, honouring the trainee's alternative choice. */
export const plannedMealTotals = (meal: MealV2, log: DayLogV2): MacroTotals =>
  rounded(plannedItems(meal, log).reduce((acc, item) => addRaw(acc, item, item.quantity || 0), emptyTotals()));

/** What the trainee reported for this meal: eaten rows only, at their actual amounts. */
export const actualMealTotals = (meal: MealV2, log: DayLogV2): MacroTotals =>
  rounded(
    mealRows(meal, log).reduce(
      (acc, item) => (isEaten(item, log) ? addRaw(acc, item, actualQuantity(item, log)) : acc),
      emptyTotals(),
    ),
  );

export const mealEatenState = (meal: MealV2, log: DayLogV2): EatenState => {
  const rows = mealRows(meal, log);
  if (!rows.length) return "none";
  const n = rows.filter((item) => isEaten(item, log)).length;
  if (n === 0) return "none";
  return n === rows.length ? "all" : "part";
};

/**
 * The meal's calorie drift from the plan, over the rows that were eaten AND came in off-plan. Rows
 * left unticked do not count: not eating something yet is not the same as eating less of it.
 */
export const mealDeviationKcal = (meal: MealV2, log: DayLogV2): number =>
  plannedItems(meal, log).reduce((sum, item) => {
    if (!isEaten(item, log)) return sum;
    const dev = foodDeviation(item, actualQuantity(item, log));
    return dev.direction === "on" ? sum : sum + dev.kcal;
  }, 0);

const dayTotals = (menu: Pick<MenuV2, "menuType" | "meals">, pick: (meal: MealV2) => Array<[FoodItemV2, number]>) =>
  rounded(
    menuMeals(menu)
      .flatMap(pick)
      .reduce((acc, [item, q]) => addRaw(acc, item, q), emptyTotals()),
  );

/** The day's plan, with the trainee's alternative-meal choices applied. */
export const dayPlannedTotals = (menu: Pick<MenuV2, "menuType" | "meals">, log: DayLogV2): MacroTotals =>
  dayTotals(menu, (meal) => plannedItems(meal, log).map((item) => [item, item.quantity || 0]));

/**
 * The day's report — what the trainee said they ate.
 *
 * **Summed at full precision and rounded ONCE**, which is a deliberate divergence from the coach
 * app's per-addition rounding and from the builder's `listTotals`. The reason is local to this
 * screen: the same day is also drawn in the calendar above, from the stored `actual_*` columns.
 * Rounding each row and again after each addition compounds the error with the number of rows —
 * across a fifteen-row day it drifted protein from 156.4 to 156.5 and calories from 1,646 to 1,647.
 * Two totals for one day, a few hundred pixels apart, disagreeing in the last digit is exactly what
 * a trainer notices and stops trusting. This replaces the local helper `FoodLogV2` grew for the
 * same reason, and keeps its behaviour.
 */
export const dayActualTotals = (menu: Pick<MenuV2, "menuType" | "meals">, log: DayLogV2): MacroTotals =>
  dayTotals(menu, (meal) =>
    mealRows(meal, log)
      .filter((item) => isEaten(item, log))
      .map((item) => [item, actualQuantity(item, log)]),
  );

/**
 * What the bars count UP TO — always the trainer's `targets`, for every menu type, never the sum of
 * the meals. `targets` is authoritative; the meals are a suggested route to it rather than an
 * exhaustive account of the day.
 */
export const dayGoal = (menu: Pick<MenuV2, "targets">): MacroSet => menu.targets;

// ── display ─────────────────────────────────────────────────────────────────

/**
 * How much of a food there is, said the way a person would say it.
 *
 * A serving name is usually itself a measurement — "100 גרם" — so pairing it with a count produces
 * "1 100 גרם", which reads as a typo. When the name starts with a number the quantity multiplies
 * INTO it: one "100 גרם" is `100 גרם`, two is `200 גרם`. Names that are a countable portion —
 * "מנה", "יחידה" — have no number to fold into and keep the count in front: `2 מנה`.
 *
 * Returned as a PAIR rather than a string because a number and a Hebrew unit inside one element are
 * reordered by the bidi algorithm, which puts the unit on the wrong side. Every caller lays the two
 * out separately instead of concatenating them.
 */
export const servingAmount = (
  quantity: number | null | undefined,
  servingName?: string | null,
): { value: number; unit: string } => {
  const raw = Number(quantity);
  const qty = quantity == null || !Number.isFinite(raw) ? 1 : raw;
  const name = (servingName || "").trim();

  // A leading number, then the unit it measures — which is OPTIONAL, because "100" and "1" are both
  // real serving names. The unit may not START with a digit, or "100" would split into 10 and a
  // unit of "0".
  const measured = name.match(/^(\d+(?:[.,]\d+)?)\s*([^\d\s].*)?$/);
  if (measured) {
    const per = Number(measured[1].replace(",", "."));
    const unit = (measured[2] || "").trim();
    // A serving of "0" is not a multiplier — keep the count and let the name speak for itself.
    return per > 0
      ? { value: Math.round(qty * per * 100) / 100, unit }
      : { value: Math.round(qty * 100) / 100, unit };
  }
  return { value: Math.round(qty * 100) / 100, unit: name };
};

// ── the view model the cards render ─────────────────────────────────────────

export type MealRowView = {
  item: FoodItemV2;
  source: RowSource;
  isPlanned: boolean;
  isEaten: boolean;
  actualQty: number;
  plannedQty: number;
  /** null for an added row — it stands in for nothing, so it cannot deviate from anything */
  deviation: { pct: number; kcal: number; direction: DeviationDirection } | null;
  /** at the ACTUAL quantity */
  nutrition: MacroSet;
  originUid: string;
  originName: string;
  groupUid: string | null;
};

/** Everything one row needs, so the component does no resolution of its own. */
export const mealRowViews = (meal: MealV2, log: DayLogV2): MealRowView[] =>
  mealRowDetails(meal, log).map(({ item, source, origin, groupUid }) => {
    const isPlanned = source !== "added";
    const actualQty = actualQuantity(item, log);
    return {
      item,
      source,
      isPlanned,
      isEaten: isEaten(item, log),
      actualQty,
      plannedQty: item.quantity || 0,
      deviation: isPlanned ? foodDeviation(item, actualQty) : null,
      nutrition: nutritionAtQuantity(item, actualQty),
      originUid: origin.uid,
      originName: origin.name,
      groupUid,
    };
  });
